/**
 * HelixScoreDemo.tsx — Galaxy-mode Helix Score Visualization
 *
 * Route: /helix-demo
 *
 * The 3D helix is the ONLY view — fullscreen galaxy environment.
 * All information lives inside the galaxy as HUD overlays:
 *   - Top-left: CP legend panel (collapsible, top:64 to clear account picker)
 *   - Top-right: live score mini-dashboard (collapsible, top:64)
 *   - Top-center: account picker (top:16, always visible)
 *   - Left/right slide-in panels when a center-pole orb is selected
 *
 * Communication with the iframe uses postMessage.
 */

import { useState, useCallback, useEffect, useRef } from "react";
import OperatorShell from "@/components/operator/OperatorShell";
import HelixScoreViz3D from "@/components/helix/HelixScoreViz3D";
import {
  HELIX_CHECKPOINTS,
  CheckpointStatus,
  computeScore,
  computeQualificationPct,
  getScoreBucket,
} from "@/lib/helix-checkpoints";
import { api } from "@/lib/session";
import { useHelixGalaxyData } from "@/lib/helix-galaxy-data";

interface AccountRow {
  id: number;
  name: string;
  industry: string | null;
}

// ─── CP Colors (mirror helix.js) ─────────────────────────────────────────────

// Brand-only checkpoint colors. Demo viz palette held to slate ramp +
// brand red for hard-kill states; teal/mint/lime/orange removed.
const CP_COLORS: Record<number, string> = {
  0: "#DC2626", // CP1 HARD-KILL red (brand)
  1: "#475569", // CP2 slate-600
  2: "#475569", // CP3 slate-600
  3: "#64748B", // CP4 slate-500
  4: "#64748B", // CP5 slate-500
  5: "#94A3B8", // CP6 slate-400
  6: "#92400E", // CP7 amber-800 (muted)
  7: "#92400E", // CP8 amber-800 (muted)
  8: "#DC2626", // CP9 HARD-KILL red (brand)
};

// ─── Status → orb color map (for helix sync) ──────────────────────────────────
const STATUS_ORB_COLOR: Record<string, number> = {
  cleared: 0x10B981, // green
  partial: 0xF59E0B, // amber
  open:    0xEF4444, // red
  na:      0x6B6B70, // muted gray
};

// ─── Initial deal state ───────────────────────────────────────────────────────

// Canonical order: CP1 Project | CP2 Need | CP3 Pain | CP4 Fit
// CP5 Budget | CP6 Timing | CP7 Authority | CP8 Org Structure and Influence Map
// CP9 Compliance — IT Internal and Industry-Specific | CP10 Legal — Contracts, SLAs, Terms
const INITIAL_STATUSES: Record<string, CheckpointStatus> = {
  cp1: "cleared",   // Project — real project on the plan
  cp2: "cleared",   // Need — recognized need confirmed
  cp3: "cleared",   // Pain — pain named, quantified, corroborated
  cp4: "partial",   // Fit — industry fit confirmed, size pending
  cp5: "cleared",   // Budget — named source, line item confirmed
  cp6: "partial",   // Timing — decision window confirmed, implementation window pending
  cp7: "open",      // Authority — decision-maker not yet confirmed
  cp8: "partial",   // Org Structure and Influence Map — org chart partial
  cp9: "open",      // Compliance — IT Internal and Industry-Specific — competitive landscape pending
  cp10: "open",     // Legal — Contracts, SLAs, Terms — barriers not yet mapped
};

// ─── Mock question data per CP (v1 — 3 answered + 3 unanswered each) ─────────

interface MockQuestion {
  id: string;
  text: string;
  hint: string;
}

interface AnsweredQuestion {
  id: string;
  text: string;
  answer: string;
  points: number;
}

const MOCK_ANSWERED: Record<number, AnsweredQuestion[]> = {
  0: [
    { id: "cp1_a1", text: "Business driver named?", answer: "Cost-down mandate from board", points: 3 },
    { id: "cp1_a2", text: "Why-now trigger typed?", answer: "M&A / acquisition", points: 3 },
    { id: "cp1_a3", text: "Pain quantified?", answer: "Yes — with $ figure attached", points: 3 },
  ],
  1: [
    { id: "cp2_a1", text: "Economic buyer identified?", answer: "Named — has veto power", points: 3 },
    { id: "cp2_a2", text: "Champion identified?", answer: "Named, coached, repeats our message", points: 3 },
    { id: "cp2_a3", text: "Multi-thread coverage?", answer: "3+ stakeholders engaged", points: 3 },
  ],
  2: [
    { id: "cp3_a1", text: "Scorecard gap present?", answer: "Excel / manual today", points: 4 },
    { id: "cp3_a2", text: "Accountability gap identified?", answer: "Named gap", points: 3 },
    { id: "cp3_a3", text: "CI culture?", answer: "Programmatic — they live it", points: 3 },
  ],
  3: [
    { id: "cp4_a1", text: "Exec sponsorship commitment?", answer: "Signed / written commitment", points: 4 },
    { id: "cp4_a2", text: "Change management readiness?", answer: "Resourced — owner + budget", points: 4 },
    { id: "cp4_a3", text: "Adoption plan in place?", answer: "Named owner + plan", points: 3 },
  ],
  4: [
    { id: "cp5_a1", text: "Cloud readiness confirmed?", answer: "Cloud OK — already on cloud", points: 3 },
    { id: "cp5_a2", text: "ERP integration path?", answer: "Specified — system + version known", points: 3 },
    { id: "cp5_a3", text: "IT stakeholder engaged?", answer: "Yes — named contact", points: 2 },
  ],
  5: [
    { id: "cp6_a1", text: "Budget confirmed?", answer: "Named line item / approved", points: 4 },
    { id: "cp6_a2", text: "CapEx vs OpEx classified?", answer: "OpEx", points: 2 },
    { id: "cp6_a3", text: "Price within range?", answer: "Within range", points: 3 },
  ],
  6: [
    { id: "cp7_a1", text: "Mutual close date set?", answer: "Joint mutual close date in writing", points: 3 },
    { id: "cp7_a2", text: "Next steps booked?", answer: "On calendar with invite accepted", points: 2 },
    { id: "cp7_a3", text: "MAP signed?", answer: "Drafted — not signed", points: 1 },
  ],
  7: [
    { id: "cp8_a1", text: "Competitive landscape known?", answer: "Named all competitors", points: 3 },
    { id: "cp8_a2", text: "Preferred status?", answer: "Signaled — not confirmed", points: 2 },
    { id: "cp8_a3", text: "Decision criteria mapped?", answer: "They wrote them — we know weights", points: 1 },
  ],
  8: [
    { id: "cp9_a1", text: "Written commitment received?", answer: "Verbal only", points: 2 },
  ],
};

const MOCK_UNANSWERED: Record<number, MockQuestion[]> = {
  0: [
    { id: "cp1_u1", text: "Cost of inaction stated?", hint: "Annualized cost of doing nothing." },
    { id: "cp1_u2", text: "L-maturity probe done?", hint: "L0–L5 maturity level identified." },
    { id: "cp1_u3", text: "CFO P&L flowthrough recognized?", hint: "Connected to specific P&L line." },
  ],
  1: [
    { id: "cp2_u1", text: "Fox (informal power broker) identified?", hint: "Person whose opinion bends the room." },
    { id: "cp2_u2", text: "Detractors mapped?", hint: "Anyone with a reason to slow this down." },
    { id: "cp2_u3", text: "Exec sponsor level?", hint: "Highest level endorsing internally." },
  ],
  2: [
    { id: "cp3_u1", text: "Alignment on goals confirmed?", hint: "Top 3 goals match across Finance, Ops, HR." },
    { id: "cp3_u2", text: "Real-time + mobile need stated?", hint: "Frontline managers need floor data." },
    { id: "cp3_u3", text: "Implementation follow-through problem?", hint: "Past initiatives died at implementation." },
  ],
  3: [
    { id: "cp4_u1", text: "Financial review cycle integration?", hint: "Initiative on QBR / monthly close agenda." },
  ],
  4: [
    { id: "cp5_u1", text: "Data environment quality?", hint: "Master data state: clean, dirty, or project." },
  ],
  5: [
    { id: "cp6_u1", text: "Procurement / approval path mapped?", hint: "Named contacts, approval levels, cycle time." },
    { id: "cp6_u2", text: "Legal / MSA status?", hint: "Existing, in-flight, or required." },
    { id: "cp6_u3", text: "ROI case built jointly?", hint: "Quantified with buyer inputs." },
  ],
  6: [
    { id: "cp7_u1", text: "Formal timeline quality?", hint: "Real formal timeline with consequence if missed." },
    { id: "cp7_u2", text: "Customer resources committed?", hint: "Named owners on their side." },
    { id: "cp7_u3", text: "RFP — did we shape it?", hint: "Cold RFPs are losses not yet booked." },
  ],
  7: [
    { id: "cp8_u1", text: "Preferred status confirmed by buyer?", hint: "Written or verbal preferred signal." },
  ],
  8: [
    { id: "cp9_u1", text: "Signed contract / PO received?", hint: "Email confirmation is not commitment." },
  ],
};

// ─── Score helpers ────────────────────────────────────────────────────────────

function scoreColor(s: number): string {
  if (s >= 86) return "#16A34A";
  if (s >= 71) return "#16A34A";
  if (s >= 41) return "#EAB308";
  return "#EF4444";
}

// ─── Status color map ─────────────────────────────────────────────────────────

const STATUS_COLOR: Record<CheckpointStatus, string> = {
  cleared: "#16A34A",
  partial: "#EAB308",
  open: "#EF4444",
  na: "#6B6B70",
};

// ─── Detect narrow viewport ───────────────────────────────────────────────────

function useIsNarrow(breakpoint = 1100): boolean {
  const [narrow, setNarrow] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setNarrow(window.innerWidth < breakpoint);
    window.addEventListener("resize", handler);
    return () => window.removeEventListener("resize", handler);
  }, [breakpoint]);
  return narrow;
}

// ─── HUD Legend Panel ─────────────────────────────────────────────────────────

function LegendHUD({ defaultExpanded }: { defaultExpanded: boolean }) {
  const [expanded, setExpanded] = useState(defaultExpanded);

  return (
    <div
      data-helix-panel="legend"
      style={{
        position: "absolute",
        top: 64,
        left: 16,
        background: "rgba(5,9,18,0.75)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 10,
        color: "#FAFAF7",
        fontFamily: "Inter, system-ui, sans-serif",
        minWidth: expanded ? 160 : 0,
        zIndex: 20,
        overflow: "hidden",
        transition: "min-width 0.2s ease",
      }}
    >
      {/* Header / toggle row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 10px",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded(e => !e)}
        title={expanded ? "Collapse legend" : "Expand legend"}
      >
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
          color: "#6B7280", whiteSpace: "nowrap",
        }}>
          CHECKPOINTS
        </span>
        <span style={{
          fontSize: 11, color: "#6B7280", lineHeight: 1,
          transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 0.2s ease",
          flexShrink: 0,
        }}>
          ▲
        </span>
      </div>

      {/* Collapsible body */}
      {expanded && (
        <div style={{ padding: "0 10px 10px 10px" }}>
          {/* Tier 1 */}
          <div style={{
            fontSize: 8, fontWeight: 700, letterSpacing: "0.1em",
            color: "#DC2626", marginBottom: 6,
          }}>T1 — QUALIFICATION</div>
          {HELIX_CHECKPOINTS.filter(cp => cp.number <= 5).map((cp, i) => (
            <div key={cp.id} style={{
              display: "flex", alignItems: "center", gap: 7, marginBottom: 4,
            }}>
              <div style={{
                width: 8, height: 8, borderRadius: "50%",
                background: CP_COLORS[i],
                boxShadow: `0 0 4px ${CP_COLORS[i]}`,
                flexShrink: 0,
              }} />
              <span style={{ color: "#c8d8f0", fontSize: 9, lineHeight: 1.2 }}>
                CP{i + 1} — {cp.name}
              </span>
            </div>
          ))}

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "7px 0" }} />

          {/* Tier 2 */}
          <div style={{
            fontSize: 8, fontWeight: 700, letterSpacing: "0.1em",
            color: "#a8d96a", marginBottom: 6,
          }}>T2 — CLOSING</div>
          {HELIX_CHECKPOINTS.filter(cp => cp.number > 5).map((cp, i) => (
            <div key={cp.id} style={{
              display: "flex", alignItems: "center", gap: 7, marginBottom: 4,
            }}>
              <div style={{
                width: 6, height: 6, borderRadius: "50%",
                background: CP_COLORS[i + 5],
                boxShadow: `0 0 3px ${CP_COLORS[i + 5]}`,
                flexShrink: 0,
              }} />
              <span style={{ color: "#a0b4cc", fontSize: 9, lineHeight: 1.2 }}>
                CP{i + 6} — {cp.name}
              </span>
            </div>
          ))}

          <div style={{ borderTop: "1px solid rgba(255,255,255,0.06)", margin: "7px 0" }} />
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{
              width: 8, height: 8, borderRadius: "50%",
              border: "2px solid #ff6b6b",
              flexShrink: 0,
            }} />
            <span style={{ color: "#ff6b6b", fontSize: 9, fontWeight: 700 }}>Critical: CP1 + CP9</span>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Score Sparkline (simple bars) ───────────────────────────────────────────

function ScoreSparkline({ history }: { history: number[] }) {
  if (history.length < 2) return null;
  const max = 100;
  const w = 60, h = 22;
  const step = w / (history.length - 1);
  const pts = history.map((v, i) => `${i * step},${h - (v / max) * h}`).join(" ");
  const lastColor = scoreColor(history[history.length - 1]);
  return (
    <svg width={w} height={h} style={{ overflow: "visible" }}>
      <polyline
        points={pts}
        fill="none"
        stroke={lastColor}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
        opacity="0.85"
      />
      <circle
        cx={(history.length - 1) * step}
        cy={h - (history[history.length - 1] / max) * h}
        r="2.5"
        fill={lastColor}
      />
    </svg>
  );
}

// ─── Mini Dashboard HUD ───────────────────────────────────────────────────────

function DashboardHUD({
  statuses,
  scoreHistory,
  defaultExpanded,
}: {
  statuses: Record<string, CheckpointStatus>;
  scoreHistory: number[];
  defaultExpanded: boolean;
}) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const score = computeScore(statuses);
  const qualPct = computeQualificationPct(statuses);
  const bucket = getScoreBucket(score);
  const color = scoreColor(score);

  // CP1–CP5 = qualification tier (60 pts total), CP6–CP10 = closing tier (40 pts total)
  const qualCheckpoints = HELIX_CHECKPOINTS.filter(cp => cp.number <= 5);
  const closingCheckpoints = HELIX_CHECKPOINTS.filter(cp => cp.number > 5);

  const t1Score = Math.round(
    qualCheckpoints.reduce((sum, cp) => {
      const s = statuses[cp.id] ?? "open";
      if (s === "cleared") return sum + cp.weight;
      if (s === "partial") return sum + cp.weight / 2;
      return sum;
    }, 0)
  );
  const t2Score = Math.round(
    closingCheckpoints.reduce((sum, cp) => {
      const s = statuses[cp.id] ?? "open";
      if (s === "cleared") return sum + cp.weight;
      if (s === "partial") return sum + cp.weight / 2;
      return sum;
    }, 0)
  );

  return (
    <div
      data-helix-panel="dashboard"
      style={{
        position: "absolute",
        top: 64,
        right: 16,
        background: "rgba(5,9,18,0.75)",
        backdropFilter: "blur(12px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 10,
        color: "#FAFAF7",
        fontFamily: "Inter, system-ui, sans-serif",
        minWidth: expanded ? 180 : 0,
        zIndex: 20,
        overflow: "hidden",
        transition: "min-width 0.2s ease",
      }}
    >
      {/* Header / toggle row */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 10px",
          gap: 8,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded(e => !e)}
        title={expanded ? "Collapse score panel" : "Expand score panel"}
      >
        <span style={{
          fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
          color: "#6B7280", whiteSpace: "nowrap",
        }}>
          DEAL SCORE
        </span>
        {!expanded && (
          <span style={{
            fontSize: 13, fontWeight: 800, color, lineHeight: 1,
          }}>
            {score}
          </span>
        )}
        <span style={{
          fontSize: 11, color: "#6B7280", lineHeight: 1,
          transform: expanded ? "rotate(0deg)" : "rotate(180deg)",
          transition: "transform 0.2s ease",
          flexShrink: 0,
        }}>
          ▲
        </span>
      </div>

      {/* Collapsible body */}
      {expanded && (
        <div style={{ padding: "0 14px 14px 14px" }}>
          {/* Score */}
          <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: 6 }}>
            <div style={{
              fontSize: 36, fontWeight: 800, color, lineHeight: 1,
              textShadow: `0 0 20px ${color}60`,
              transition: "color 0.4s",
            }}>
              {score}
            </div>
            <div style={{ paddingBottom: 4 }}>
              <div style={{ fontSize: 10, color: "#6B7280", lineHeight: 1 }}>/100</div>
              <div style={{ marginTop: 2 }}>
                <ScoreSparkline history={scoreHistory} />
              </div>
            </div>
          </div>

          {/* Bucket */}
          <div style={{
            fontSize: 11, fontWeight: 700, color,
            letterSpacing: "0.04em", marginBottom: 8,
            transition: "color 0.4s",
          }}>
            {bucket === "Strong" ? "Strong qualified opportunity" :
              bucket === "Qualified" ? "Qualified opportunity" :
              bucket === "Developing" ? "Developing — needs attention" :
              "Early stage — high risk"}
          </div>

          {/* T1 / T2 sub-totals */}
          <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
            <div style={{
              background: "rgba(84,199,236,0.12)",
              border: "1px solid rgba(84,199,236,0.25)",
              borderRadius: 6, padding: "4px 8px",
              fontSize: 10,
            }}>
              <span style={{ color: "#6B7280" }}>T1 </span>
              <span style={{ color: "#DC2626", fontWeight: 700 }}>{t1Score}/60</span>
              <span style={{ color: "#6B7280" }}> pts</span>
            </div>
            <div style={{
              background: "rgba(168,217,106,0.12)",
              border: "1px solid rgba(168,217,106,0.25)",
              borderRadius: 6, padding: "4px 8px",
              fontSize: 10,
            }}>
              <span style={{ color: "#6B7280" }}>T2 </span>
              <span style={{ color: "#a8d96a", fontWeight: 700 }}>{t2Score}/40</span>
              <span style={{ color: "#6B7280" }}> pts</span>
            </div>
          </div>

          {/* Qual % badge */}
          <div style={{ display: "flex", gap: 5, marginBottom: 8 }}>
            <div style={{
              fontSize: 10, color: "#6B7280", alignSelf: "center",
            }}>
              CP1–CP5 qualified: <span style={{ color, fontWeight: 700 }}>{qualPct}%</span>
            </div>
          </div>

          <div style={{ fontSize: 8, color: "#525258", letterSpacing: "0.08em" }}>
            VERTEX SYSTEMS · DEAL DNA
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Answered Panel ───────────────────────────────────────────────────────────

function AnsweredPanel({
  cpIndex,
  cpName,
  cpColor,
  answered,
  onClose,
}: {
  cpIndex: number;
  cpName: string;
  cpColor: string;
  answered: AnsweredQuestion[];
  onClose: () => void;
}) {
  return (
    <div
      style={{
        position: "absolute",
        left: 16,
        top: "50%",
        transform: "translateY(-50%)",
        width: 280,
        maxHeight: "55vh",
        background: "rgba(5,9,18,0.85)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 30,
        animation: "slideInLeft 0.3s ease",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        background: `linear-gradient(90deg, ${cpColor}18, transparent)`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}>
        <div>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
            color: cpColor, marginBottom: 3,
          }}>
            CP{cpIndex + 1} · ANSWERED
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#FAFAF7" }}>
            {cpName}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close panel"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.5)",
            fontSize: 18,
            lineHeight: 1,
            padding: "0 0 0 8px",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* List */}
      <div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
        {answered.length === 0 ? (
          <div style={{
            padding: "20px 16px", textAlign: "center",
            color: "#525258", fontSize: 11,
          }}>
            No answered questions yet.
          </div>
        ) : answered.map(q => (
          <div key={q.id} style={{
            padding: "10px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
          }}>
            <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 3, lineHeight: 1.3 }}>
              {q.text}
            </div>
            <div style={{ fontSize: 11, color: "#c8d8f0", fontWeight: 600, marginBottom: 3 }}>
              {q.answer}
            </div>
            <div style={{
              fontSize: 10, fontWeight: 700,
              color: q.points > 0 ? "#16A34A" : "#EF4444",
            }}>
              {q.points > 0 ? `+${q.points} pts` : `${q.points} pts`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Unanswered Panel ─────────────────────────────────────────────────────────

function UnansweredPanel({
  cpIndex,
  cpName,
  cpColor,
  unanswered,
  onAnswer,
  onClose,
}: {
  cpIndex: number;
  cpName: string;
  cpColor: string;
  unanswered: MockQuestion[];
  onAnswer: (qId: string, text: string) => void;
  onClose: () => void;
}) {
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  if (unanswered.length === 0) return null;

  return (
    <div
      style={{
        position: "absolute",
        right: 16,
        top: "50%",
        transform: "translateY(-50%)",
        width: 295,
        maxHeight: "55vh",
        background: "rgba(5,9,18,0.85)",
        backdropFilter: "blur(16px)",
        border: "1px solid rgba(255,255,255,0.1)",
        borderRadius: 12,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
        zIndex: 30,
        animation: "slideInRight 0.3s ease",
        fontFamily: "Inter, system-ui, sans-serif",
      }}
    >
      {/* Header */}
      <div style={{
        padding: "12px 16px",
        borderBottom: "1px solid rgba(255,255,255,0.08)",
        background: `linear-gradient(270deg, ${cpColor}18, transparent)`,
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
      }}>
        <div>
          <div style={{
            fontSize: 9, fontWeight: 700, letterSpacing: "0.1em",
            color: cpColor, marginBottom: 3,
          }}>
            CP{cpIndex + 1} · OPEN QUESTIONS
          </div>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#FAFAF7" }}>
            {cpName}
          </div>
        </div>
        <button
          onClick={onClose}
          aria-label="Close panel"
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            color: "rgba(255,255,255,0.5)",
            fontSize: 18,
            lineHeight: 1,
            padding: "0 0 0 8px",
            flexShrink: 0,
          }}
        >
          ×
        </button>
      </div>

      {/* List */}
      <div style={{ overflowY: "auto", flex: 1, padding: "8px 0" }}>
        {unanswered.map((q) => (
          <div key={q.id} style={{
            padding: "10px 16px",
            borderBottom: "1px solid rgba(255,255,255,0.04)",
          }}>
            <div style={{ fontSize: 11, color: "#c8d8f0", fontWeight: 600, marginBottom: 3, lineHeight: 1.3 }}>
              {q.text}
            </div>
            {q.hint && (
              <div style={{ fontSize: 10, color: "#525258", marginBottom: 6, lineHeight: 1.3 }}>
                {q.hint}
              </div>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <input
                type="text"
                value={drafts[q.id] ?? ""}
                onChange={(e) => setDrafts(prev => ({ ...prev, [q.id]: e.target.value }))}
                placeholder="Enter answer..."
                style={{
                  flex: 1,
                  background: "rgba(255,255,255,0.06)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  borderRadius: 6,
                  padding: "5px 8px",
                  color: "#FAFAF7",
                  fontSize: 11,
                  outline: "none",
                  fontFamily: "Inter, system-ui, sans-serif",
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && drafts[q.id]?.trim()) {
                    onAnswer(q.id, drafts[q.id].trim());
                    setDrafts(prev => ({ ...prev, [q.id]: "" }));
                  }
                }}
              />
              <button
                onClick={() => {
                  const val = drafts[q.id]?.trim();
                  if (val) {
                    onAnswer(q.id, val);
                    setDrafts(prev => ({ ...prev, [q.id]: "" }));
                  }
                }}
                style={{
                  background: cpColor + "22",
                  border: `1px solid ${cpColor}55`,
                  borderRadius: 6,
                  color: cpColor,
                  fontSize: 11,
                  fontWeight: 700,
                  padding: "5px 10px",
                  cursor: "pointer",
                  fontFamily: "Inter, system-ui, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Save
              </button>
            </div>
          </div>
        ))}
      </div>

      <div style={{
        padding: "8px 16px",
        borderTop: "1px solid rgba(255,255,255,0.06)",
        fontSize: 9, color: "#525258",
      }}>
        Press Enter or Save to record answer
      </div>
    </div>
  );
}

// ─── Main galaxy page ─────────────────────────────────────────────────────────

export default function HelixScoreDemo() {
  // ── Viewport width check — collapse HUDs on narrow screens ────────────────
  const isNarrow = useIsNarrow(1100);

  // ── Iframe ref (for postMessage sync) ─────────────────────────────────────
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // ── Account picker ────────────────────────────────────────────────────────
  // Tenant isolation: /api/accounts is tenant-scoped server-side. Helix tables
  // (account_cp_status, account_answers) join by account_id, so loading them
  // for an account in this tenant cannot leak cross-tenant rows.
  const [accounts, setAccounts] = useState<AccountRow[]>([]);
  const [selectedAccountId, setSelectedAccountId] = useState<number | null>(null);
  const [accountsLoading, setAccountsLoading] = useState(true);

  useEffect(() => {
    api<AccountRow[]>("/api/accounts")
      .then((data) => setAccounts(Array.isArray(data) ? data : []))
      .catch(() => setAccounts([]))
      .finally(() => setAccountsLoading(false));
  }, []);

  // Live data hook — returns empty maps when accountId is null.
  const live = useHelixGalaxyData(selectedAccountId);

  // When no account is selected, fall back to mock so the galaxy still tells
  // a coherent demo story.
  const useLive = selectedAccountId != null && !live.loading && !live.error;

  // Local working copy of statuses/answered/unanswered. Reset whenever the
  // source (mock vs live account) changes. Local edits via handleAnswer are
  // optimistic-only in this v1 — they do not yet persist back to Supabase.
  const [statuses, setStatuses] = useState<Record<string, CheckpointStatus>>(INITIAL_STATUSES);
  const [answered, setAnswered] = useState<Record<number, AnsweredQuestion[]>>(
    MOCK_ANSWERED as Record<number, AnsweredQuestion[]>
  );
  const [unanswered, setUnanswered] = useState<Record<number, MockQuestion[]>>(
    MOCK_UNANSWERED as Record<number, MockQuestion[]>
  );
  const [scoreHistory, setScoreHistory] = useState<number[]>([computeScore(INITIAL_STATUSES)]);

  useEffect(() => {
    if (useLive) {
      setStatuses(live.statuses);
      setAnswered(live.answered as Record<number, AnsweredQuestion[]>);
      setUnanswered(live.unanswered as Record<number, MockQuestion[]>);
      setScoreHistory([computeScore(live.statuses)]);
    } else if (selectedAccountId == null) {
      setStatuses(INITIAL_STATUSES);
      setAnswered(MOCK_ANSWERED as Record<number, AnsweredQuestion[]>);
      setUnanswered(MOCK_UNANSWERED as Record<number, MockQuestion[]>);
      setScoreHistory([computeScore(INITIAL_STATUSES)]);
    }
  }, [useLive, selectedAccountId, live.statuses, live.answered, live.unanswered]);

  // ── Sync statuses → helix iframe orbs via postMessage ────────────────────
  const postStatusesToIframe = useCallback((currentStatuses: Record<string, CheckpointStatus>) => {
    const iframe = iframeRef.current;
    if (!iframe?.contentWindow) return;
    iframe.contentWindow.postMessage(
      { target: "mi6-helix", type: "set-cp-statuses", statuses: currentStatuses },
      "*"
    );
  }, []);

  // Fire whenever statuses change
  useEffect(() => {
    postStatusesToIframe(statuses);
  }, [statuses, postStatusesToIframe]);

  // Also fire on iframe load (iframe may load after statuses are already set)
  const handleIframeLoad = useCallback(() => {
    // Small delay to let helix.js finish initialising its message listener
    setTimeout(() => postStatusesToIframe(statuses), 200);
  }, [statuses, postStatusesToIframe]);

  // Selected CP state (from postMessage events from the iframe)
  const [selectedCpIndex, setSelectedCpIndex] = useState<number | null>(null);
  const [selectedCpInfo, setSelectedCpInfo] = useState<{
    cpIndex: number; cpNumber: number; cpName: string;
    hardKill: boolean; color: string;
  } | null>(null);

  // Listen for postMessages from the helix iframe
  useEffect(() => {
    function handleMessage(e: MessageEvent) {
      if (!e.data || e.data.source !== "mi6-helix") return;

      if (e.data.type === "cp-selected") {
        const { cpIndex, cpNumber, cpName, hardKill, color } = e.data;
        setSelectedCpIndex(cpIndex);
        setSelectedCpInfo({ cpIndex, cpNumber, cpName, hardKill, color });
      } else if (e.data.type === "cp-deselected") {
        setSelectedCpIndex(null);
        setSelectedCpInfo(null);
      }
    }
    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, []);

  // When a question is answered, move it from unanswered → answered and update score
  const handleAnswer = useCallback((cpIdx: number, qId: string, answerText: string) => {
    const q = (unanswered[cpIdx] ?? []).find(x => x.id === qId);
    if (!q) return;

    // Remove from unanswered
    setUnanswered(prev => ({
      ...prev,
      [cpIdx]: (prev[cpIdx] ?? []).filter(x => x.id !== qId),
    }));

    // Add to answered with mock points (1–3)
    const pts = Math.floor(Math.random() * 3) + 1;
    const newAns: AnsweredQuestion = { id: qId, text: q.text, answer: answerText, points: pts };
    setAnswered(prev => ({
      ...prev,
      [cpIdx]: [...(prev[cpIdx] ?? []), newAns],
    }));

    // Update the checkpoint status toward "cleared"
    const cpId = HELIX_CHECKPOINTS[cpIdx]?.id;
    if (cpId) {
      setStatuses(prev => {
        const current = prev[cpId] ?? "open";
        const next: CheckpointStatus =
          current === "open" ? "partial" :
          current === "partial" ? "cleared" : current;
        const updated = { ...prev, [cpId]: next };
        // Track score history
        const newScore = computeScore(updated);
        setScoreHistory(h => [...h.slice(-19), newScore]);
        return updated;
      });
    }
  }, [unanswered]);

  // Close panels when clicking away (Escape key or clicking the 3D canvas area)
  const handleDeselectCP = useCallback(() => {
    setSelectedCpIndex(null);
    setSelectedCpInfo(null);
    // Tell iframe to deselect
    const iframe = iframeRef.current;
    iframe?.contentWindow?.postMessage({ target: "mi6-helix", type: "deselect-cp" }, "*");
  }, []);

  // Escape key handler — close overlay panels (P0-2 fix)
  useEffect(() => {
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape" && selectedCpInfo) {
        handleDeselectCP();
      }
    }
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedCpInfo, handleDeselectCP]);

  return (
    <OperatorShell breadcrumbs={[{ label: "Helix Demo" }]} pageTitle="Helix Score Demo" pageMeta="DNA visualization">
      {/* Top nav band */}

      {/* Galaxy viewport — fills everything below the nav */}
      {/* Click on the backdrop (not on a panel) closes panels (P0-2 fix) */}
      <div
        data-tour="helix-score-viz"
        onClick={(e) => {
          // Close panels when clicking the 3D canvas backdrop (not the panels themselves)
          const target = e.target as HTMLElement;
          if (selectedCpInfo && !target.closest('[data-helix-panel]')) {
            handleDeselectCP();
          }
        }}
        style={{
          position: "relative",
          height: `calc(100vh - ${navH}px)`,
          width: "100%",
          overflow: "hidden",
        }}
      >
        {/* 3D Helix fills 100% of the galaxy viewport */}
        <HelixScoreViz3D
          height="100%"
          disablePaywall
          autoRotate
          iframeRef={iframeRef}
          onLoad={handleIframeLoad}
        />

        {/* ── HUD overlays (always visible, collapsible) ── */}
        <LegendHUD defaultExpanded={!isNarrow} />
        <DashboardHUD statuses={statuses} scoreHistory={scoreHistory} defaultExpanded={!isNarrow} />

        {/* ── Account picker HUD ── */}
        <div
          data-helix-panel="account-picker"
          style={{
            position: "absolute",
            top: 16,
            left: "50%",
            transform: "translateX(-50%)",
            background: "rgba(5,9,18,0.78)",
            backdropFilter: "blur(12px)",
            border: "1px solid rgba(255,255,255,0.1)",
            borderRadius: 10,
            padding: "8px 12px",
            color: "#FAFAF7",
            fontFamily: "Inter, system-ui, sans-serif",
            fontSize: 12,
            zIndex: 25,
            display: "flex",
            alignItems: "center",
            gap: 10,
            minWidth: 320,
          }}
        >
          <span style={{
            fontSize: 9, fontWeight: 700, letterSpacing: "0.12em",
            color: "#6B7280",
          }}>
            ACCOUNT
          </span>
          {accountsLoading ? (
            <span style={{ color: "#8E8E94", fontSize: 11 }}>Loading...</span>
          ) : (
            <select
              value={selectedAccountId == null ? "" : String(selectedAccountId)}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedAccountId(v === "" ? null : Number(v));
              }}
              style={{
                flex: 1,
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.12)",
                borderRadius: 6,
                padding: "4px 8px",
                color: "#FAFAF7",
                fontSize: 12,
                outline: "none",
                fontFamily: "Inter, system-ui, sans-serif",
              }}
            >
              <option value="" style={{ background: "#0A0A0C" }}>
                -- Demo (mock data) --
              </option>
              {accounts.map((a) => (
                <option key={a.id} value={String(a.id)} style={{ background: "#0A0A0C" }}>
                  {a.name}{a.industry ? ` (${a.industry})` : ""}
                </option>
              ))}
            </select>
          )}
          {selectedAccountId != null && live.loading && (
            <span style={{ color: "#a8d96a", fontSize: 10 }}>Loading...</span>
          )}
          {selectedAccountId != null && live.error && (
            <span style={{ color: "#EF4444", fontSize: 10 }} title={live.error}>
              Error
            </span>
          )}
        </div>

        {/* ── Hint bar at bottom ── */}
        <div style={{
          position: "absolute",
          bottom: 16,
          left: "50%",
          transform: "translateX(-50%)",
          background: "rgba(5,9,18,0.6)",
          backdropFilter: "blur(8px)",
          border: "1px solid rgba(255,255,255,0.08)",
          borderRadius: 20,
          padding: "5px 16px",
          color: "#6B7280",
          fontSize: 11,
          pointerEvents: "none",
          whiteSpace: "nowrap",
          zIndex: 20,
        }}>
          Tap center-spine orbs to navigate checkpoints
          <span style={{ margin: "0 8px", opacity: 0.4 }}>·</span>
          Drag to rotate
          <span style={{ margin: "0 8px", opacity: 0.4 }}>·</span>
          Scroll to zoom
        </div>

        {/* ── CP dismiss button (when a CP is selected) ── */}
        {selectedCpInfo && (
          <button
            onClick={handleDeselectCP}
            style={{
              position: "absolute",
              bottom: 52,
              left: "50%",
              transform: "translateX(-50%)",
              background: "rgba(5,9,18,0.8)",
              backdropFilter: "blur(8px)",
              border: `1px solid ${selectedCpInfo.color}55`,
              borderRadius: 20,
              padding: "5px 18px",
              color: selectedCpInfo.color,
              fontSize: 11,
              fontWeight: 600,
              cursor: "pointer",
              zIndex: 30,
              fontFamily: "Inter, system-ui, sans-serif",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <span style={{
              width: 8, height: 8, borderRadius: "50%",
              background: selectedCpInfo.color,
              display: "inline-block",
            }} />
            CP{selectedCpInfo.cpNumber} — {selectedCpInfo.cpName}
            <span style={{ opacity: 0.55, marginLeft: 4 }}>×</span>
          </button>
        )}

        {/* ── Side panels (slide in when CP selected) ── */}
        {selectedCpInfo && (
          <>
            <div data-helix-panel="answered">
              <AnsweredPanel
                cpIndex={selectedCpInfo.cpIndex}
                cpName={selectedCpInfo.cpName}
                cpColor={selectedCpInfo.color}
                answered={answered[selectedCpInfo.cpIndex] ?? []}
                onClose={handleDeselectCP}
              />
            </div>
            <div data-helix-panel="unanswered">
              <UnansweredPanel
                cpIndex={selectedCpInfo.cpIndex}
                cpName={selectedCpInfo.cpName}
                cpColor={selectedCpInfo.color}
                unanswered={unanswered[selectedCpInfo.cpIndex] ?? []}
                onAnswer={(qId, text) => handleAnswer(selectedCpInfo.cpIndex, qId, text)}
                onClose={handleDeselectCP}
              />
            </div>
          </>
        )}
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-24px) translateY(-50%); }
          to   { opacity: 1; transform: translateX(0) translateY(-50%); }
        }
        @keyframes slideInRight {
          from { opacity: 0; transform: translateX(24px) translateY(-50%); }
          to   { opacity: 1; transform: translateX(0) translateY(-50%); }
        }
      `}</style>
    </OperatorShell>
  );
}
